import { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { Glow, Icon, LinkButton } from '@/components/ui';
import { paths } from '@/routes/paths';

import { JourneyCurve } from './JourneyCurve';
import { JourneyStep } from './JourneyStep';
import { NODES, atViewBox } from './journeyGeometry';
import {
  HEADLINE_FROM,
  HEADLINE_TO,
  makeReveal,
  playWhenSeen,
  zoomOutOnScroll,
} from './useSectionReveal';

gsap.registerPlugin(ScrollTrigger);

/**
 * Step copy, where each block sits in the curve's viewBox, and where its ghost
 * numeral sits relative to that block. The numeral offsets differ per step
 * because the design hand-places them -- beside the copy on 1, above it on 2
 * and 3 -- and there is no rule underneath to derive.
 *
 * `at` is the title's top-left, mapped from the Figma frame the same way the
 * nodes in journeyGeometry are, so a step and the node it hangs from were
 * read off the one drawing. Note that step 2 sits well left of its dropline:
 * the frame has the line fall past the numeral's right-hand side, not into
 * the title.
 *
 * The numeral offsets are measured from the digit's ink, not its box. At
 * 9.75rem the box carries about 22px of dead space above the figure and 31px
 * below it, and those are folded into the offsets here -- so the 2 and 3 clear
 * their titles by the frame's 43px, and the 1 tops out 34px above its. They
 * are `top` and margin rather than translates, for the reason JourneyStep
 * gives: the numeral's transform belongs to the timeline.
 */
const STEPS = [
  {
    step: 1,
    title: 'Create your profile',
    description: 'Showcase your skills and goals.',
    at: [137, 509],
    numeral: 'lg:-top-14 lg:bottom-auto lg:mb-0 lg:-left-[5.5rem]',
  },
  {
    step: 2,
    title: 'Discover your match',
    description: 'Browse relevant internships tailored to you.',
    at: [508, 471],
    numeral: 'lg:left-0 lg:mb-3',
  },
  {
    step: 3,
    title: 'Connect & get started',
    description: 'Apply, review, and connect to move forward.',
    at: [906, 193],
    numeral: 'lg:left-[2.375rem] lg:mb-3',
  },
];

/**
 * Beats on the master timeline, in seconds.
 *
 * The order is the story. Step one is standing before the reader has scrolled
 * anywhere: its dot lands under the heading and the line runs out to meet it
 * while the copy beside it is still arriving -- so `firstDot` and `firstDraw`
 * are beats on the arrival timeline, alongside the headline. Everything after
 * that is earned by scrolling -- the line sets off, and dots two and three
 * appear only as it reaches them, each stopping to let its step introduce
 * itself. So the line is what carries the reader from step to step rather than
 * something running underneath them, and no dot is on screen before the
 * journey has reached it.
 */
const BEAT = {
  headline: 0,
  works: 0.45,
  firstDot: 0.5,
  firstDraw: 0.8,
  subtitle: 1.5,
  description: 1.8,
  buttons: 2.1,
};

/**
 * Seconds the line would take to travel the whole path.
 *
 * Each leg's duration is its share of the path length times this, so the legs
 * share one average speed. Giving every leg the same duration instead would
 * make the line crawl between the close-together dots and race between the
 * far-apart ones.
 */
const DRAW_SPEED = 5.5;

/** How long a step holds the stage before the line sets off for the next. */
const HOLD = 0.9;

/**
 * Seconds of scrubbed timeline before the line leaves step one.
 *
 * Small on purpose: it exists so that nothing sits at the timeline's first
 * frame (see where it is used), and at SCROLL_PER_SECOND it costs the reader
 * under thirty pixels of wheel.
 */
const LEAD = 0.25;

/**
 * How far past the first dot the line runs on arrival, as a fraction of the
 * leg to the second.
 *
 * A line that stops dead on the dot says the journey is over; one that trails
 * off past it says there is more, and the scroll is how to get it. A third of
 * the leg keeps the run-on well short of where dot two will appear.
 */
const RUN_ON = 0.3;

/**
 * Pixels of scroll each second of the timeline costs while the section is
 * pinned.
 *
 * The timeline is authored in seconds because the beats are a rhythm, and this
 * is the exchange rate to the only clock a scrubbed animation actually has.
 * Higher means the reader works harder for each step; lower means the whole
 * journey flies past in one flick of the wheel.
 */
const SCROLL_PER_SECOND = 110;

/**
 * Offsets from the line arriving at a dot to each of that step's reactions.
 *
 * The numeral is negative on purpose: it starts forming while the line is
 * still closing the last stretch, so the two read as one event -- the line
 * bringing the number with it -- rather than the line stopping and the number
 * turning up afterwards.
 */
const REACTION = { pulse: 0, drop: 0.1, numeral: -0.2, title: 0.35, description: 0.55 };

/**
 * Where along the path each node sits, as a 0-1 fraction of its length.
 *
 * Measured off the rendered path rather than guessed, because the whole
 * illusion depends on a node firing at the instant the line touches it: the
 * arc length between two nodes is not proportional to the horizontal distance
 * between them, so eyeballed timings drift visibly on the steep climb into
 * step 3. Sampling is cheap and runs once.
 */
function measureNodeProgress(pathEl) {
  const total = pathEl.getTotalLength();
  const SAMPLES = 600;

  return NODES.map((node) => {
    let best = 0;
    let bestDistance = Infinity;

    for (let i = 0; i <= SAMPLES; i += 1) {
      const length = (i / SAMPLES) * total;
      const point = pathEl.getPointAtLength(length);
      const distance = (point.x - node.x) ** 2 + (point.y - node.y) ** 2;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = length / total;
      }
    }

    return best;
  });
}

/**
 * "How Wonder Works" -- a scroll-driven journey rather than a section that
 * fades in.
 *
 * Above lg a single timeline plays itself out the first time the section is
 * on screen: the heading rises, the line starts drawing, and each node,
 * numeral and label fires at the moment the line physically reaches it. That
 * coupling is the point, so the node timings are measured from the path
 * instead of authored.
 *
 * Below lg there is no room for the curve, so the same elements get ordinary
 * on-enter reveals. Under `prefers-reduced-motion` nothing is
 * touched at all -- the markup renders in its final state on its own, so
 * reduced motion is the no-op branch rather than a second set of animations to
 * keep in sync.
 */
export function HowWonderWorks() {
  const root = useRef(null);

  useLayoutEffect(() => {
    const scope = root.current;
    const media = gsap.matchMedia();

    media.add(
      {
        isDesktop: '(min-width: 1024px)',
        reduced: '(prefers-reduced-motion: reduce)',
      },
      (context) => {
        const { isDesktop, reduced } = context.conditions;

        // Everything already renders in its finished state, so honouring the
        // preference means declining to animate rather than animating to it.
        if (reduced) return;

        const select = gsap.utils.selector(scope);
        const numerals = select('.js-numeral');
        const titles = select('.js-step h3');
        const descriptions = select('.js-step p');

        if (!isDesktop) {
          const headingTimeline = gsap.timeline({ paused: true });
          const revealHeading = makeReveal(headingTimeline, select);

          // The same hinge as the desktop timeline, tightened -- the two
          // layouts should not introduce the section differently.
          revealHeading(
            '.js-head-line, .js-head-works',
            { rotationX: -82, y: -12, opacity: 0, filter: 'blur(12px)' },
            {
              rotationX: 0,
              duration: 1,
              stagger: 0.12,
              ease: 'power3.out',
              transformOrigin: '50% 100%',
            },
          );
          revealHeading(
            '.js-subtitle, .js-description',
            { clipPath: 'inset(0 100% 0 0)', x: -12, opacity: 0 },
            { clipPath: 'inset(0 0% 0 0)', duration: 0.8, stagger: 0.12, ease: 'power3.out' },
          );
          revealHeading(
            '.js-cta',
            { x: -32, skewX: -12, scale: 0.9, opacity: 0 },
            { skewX: 0, duration: 0.8, stagger: 0.14, ease: 'back.out(1.7)' },
          );

          playWhenSeen(select('.js-heading')[0], headingTimeline, { start: 'top 85%' });

          select('.js-step').forEach((step, index) => {
            const stepTimeline = gsap.timeline({ paused: true });
            const revealStep = makeReveal(stepTimeline, select);

            revealStep(
              [numerals[index]],
              { y: 60, scale: 0.75, opacity: 0, filter: 'blur(14px)' },
              { duration: 0.9, ease: 'power3.out' },
            );
            revealStep([titles[index]], { y: 22, opacity: 0 }, { duration: 0.6 }, 0.25);
            revealStep([descriptions[index]], { y: 16, opacity: 0 }, { duration: 0.6 }, 0.38);

            playWhenSeen(step, stepTimeline, { start: 'top 85%' });
          });

          return;
        }

        const progress = measureNodeProgress(select('.js-wave')[0]);

        // The journey: the line, the three dots and the copy hanging off
        // them. This is the half the scrollbar drives; the heading above it
        // has its own timeline that plays itself on the way in.
        //
        // `smoothOrigin: false` on both timelines, because the SVG tweens set
        // an origin of some kind and step one's now live on the arrival
        // timeline too. Left on -- the default -- GSAP re-solves the origin
        // against the element's bounding box and banks the difference as a
        // translation it never takes back: each node group settles a radius up
        // and to the left of the curve it is meant to sit on, and the
        // droplines hang off their dots. Nothing here moves its origin
        // mid-tween, which is the only thing the smoothing exists for. The
        // guard was once on the scrubbed timeline alone, and the moment step
        // one moved off it the first dot drifted by exactly its radius while
        // two and three stayed put.
        const defaults = { ease: 'power3.out', smoothOrigin: false };

        // Built paused and with no trigger attached yet, so the trigger is
        // created only once every tween exists.
        const timeline = gsap.timeline({ paused: true, defaults });
        const reveal = makeReveal(timeline, select);

        // The heading plays itself the first time the section comes into
        // view, on its own timeline and its own trigger. It is deliberately
        // not on the scrubbed one: the copy is not a step of the journey, and
        // scrubbing it would mean the reader had to drag the wheel to finish
        // reading a sentence. Only the numbered steps are worth the scroll.
        //
        // The heading rises out of its masks on the same slow three seconds
        // as every other heading on the page.
        //
        // `clipPath` is named on both ends of the copy tweens below. `reveal`
        // only knows how to settle the properties in its own neutral table,
        // and it is not in it, so an unstated destination would leave the
        // copy clipped for good.
        const headTimeline = gsap.timeline({ paused: true, defaults });
        const revealHead = makeReveal(headTimeline, select);

        revealHead('.js-head-line', HEADLINE_FROM, HEADLINE_TO, BEAT.headline);
        revealHead('.js-head-works', HEADLINE_FROM, HEADLINE_TO, BEAT.works);
        // Not a `reveal`: text-shadow has no neutral to settle on, and the
        // point is a flare that comes and goes rather than a value that
        // arrives. `yoyo` spends the second half taking it back off.
        headTimeline.fromTo(
          select('.js-head-works'),
          { textShadow: '0 0 0 rgba(196, 176, 250, 0)' },
          {
            textShadow: '0 0 32px rgba(196, 176, 250, 0.55)',
            duration: 0.45,
            ease: 'sine.inOut',
            repeat: 1,
            yoyo: true,
          },
          BEAT.works + 0.9,
        );
        // The two paragraphs are wiped in from the left rather than faded up,
        // so the copy reads as being written out under the heading that has
        // just stood up.
        revealHead(
          '.js-subtitle',
          { clipPath: 'inset(0 100% 0 0)', x: -14, opacity: 0 },
          { clipPath: 'inset(0 0% 0 0)', duration: 0.9 },
          BEAT.subtitle,
        );
        revealHead(
          '.js-description',
          { clipPath: 'inset(0 100% 0 0)', x: -14, opacity: 0 },
          { clipPath: 'inset(0 0% 0 0)', duration: 1 },
          BEAT.description,
        );
        // The buttons come in on a skew and straighten as they settle, which
        // reads as them being dealt into place.
        revealHead(
          '.js-cta',
          { x: -40, skewX: -12, scale: 0.9, opacity: 0 },
          { skewX: 0, duration: 0.9, stagger: 0.18, ease: 'back.out(1.7)' },
          BEAT.buttons,
        );

        // Fires while the section is still on its way up the screen, well
        // before the pin below takes hold, so the copy has read itself in by
        // the time the journey is ready to be scrolled through.
        //
        // Once only, unlike the other sections. Scrolling back up into this
        // one arrives from inside the pin, with the scrubbed timeline already
        // writing the first dot and the first leg of the line; restarting the
        // arrival on top of that had the two fighting over the same stroke
        // and the section flicked. Played once, the copy simply stays where
        // it landed and the scrub alone drives the return.
        playWhenSeen(scope, headTimeline, { start: 'top 80%', once: true });

        // Step one belongs to the arrival, not the scroll. Its dot lands under
        // the heading and the line runs out to meet it while the copy beside
        // it is still being written -- all on `headTimeline`, which plays
        // itself the moment the section comes into view. Only steps two and
        // three are earned by scrolling. An earlier pass put step one on the
        // scrubbed timeline as well, and since a scrubbed timeline sits at
        // zero until the pin engages, a reader arriving at the section found
        // the heading alone on an otherwise empty screen.
        //
        // `transformOrigin` rather than `svgOrigin` for the dots because each
        // node group is scaled about the centre of its own bounding box, which
        // for two concentric circles is the dot itself. Dots two and three are
        // placed by the line further down, each popping in just ahead of the
        // stroke that is about to land on it, so the reader watches the
        // journey build rather than being shown its destinations up front.
        const nodeEls = select('.js-node');
        const wave = select('.js-wave, .js-wave-glow');
        const pulses = select('.js-pulse');
        const drops = select('.js-drop');

        const revealNode = (revealOn, index, at) =>
          revealOn(
            [nodeEls[index]],
            { scale: 0, opacity: 0 },
            { duration: 0.5, ease: 'back.out(1.5)', transformOrigin: '50% 50%' },
            at,
          );

        // The path is one continuous stroke revealed a leg at a time. With
        // `pathLength="1"` and a dash as long as the path, an offset of
        // `1 - p` shows exactly the first `p` of it -- so each leg is a move
        // between two offsets, and the line grows without ever being redrawn.
        // Both timelines write the same offset; they never run at once, since
        // the arrival has finished before the pin can engage.
        const setOffset = (value) => {
          for (const element of wave) element.style.strokeDashoffset = String(value);
        };

        setOffset(1);

        // Each leg tweens a plain number and writes the offset by hand rather
        // than naming `strokeDashoffset` as the tween property. Tweened
        // directly, GSAP resolves it as a CSS length and the value it lands on
        // never reaches this path -- the tween runs, its callbacks fire, and
        // the stroke does not move. Driving a number and assigning it is one
        // line longer and leaves nothing to interpret.
        const drawLeg = (on, from, to, at, ease = 'power1.inOut') => {
          const duration = DRAW_SPEED * (from - to);
          const state = { offset: from };

          on.to(
            state,
            {
              offset: to,
              duration,
              // Eased, not linear. The line halts at every dot, so each leg is
              // a journey in its own right: it pulls away from a standstill
              // and settles into the next one. A linear tween leaves at full
              // speed from nothing, which is what made this snap.
              ease,
              onUpdate: () => setOffset(state.offset),
            },
            at,
          );

          return at + duration;
        };

        // Everything a step does once the line has reached its dot, on
        // whichever timeline that leg was drawn on.
        const react = (on, revealOn, index, arrival) => {
          const origin = `${NODES[index].x} ${NODES[index].y}`;

          // The energy the arriving line dumps into the dot.
          //
          // `immediateRender: false` is load-bearing here and nowhere else in
          // this timeline. A `fromTo` writes its start values the moment it is
          // built, which every other tween wants -- their start state is
          // invisible, so they simply stay hidden until their beat. This one's
          // start state is a ring at 0.9 opacity, and it was being painted the
          // instant the section mounted: three rings visible before the line
          // had drawn a pixel, and scaled to 0.6 about the SVG's own origin
          // rather than about `svgOrigin`, which is only read once the tween
          // actually starts -- so they sat up in the heading at 60% of the
          // distance from the corner. Deferring the render leaves the markup's
          // own `opacity="0"` in place until the line arrives.
          on.fromTo(
            pulses[index],
            { scale: 0.6, opacity: 0.9 },
            {
              scale: 2.8,
              opacity: 0,
              duration: 0.9,
              ease: 'power2.out',
              svgOrigin: origin,
              immediateRender: false,
            },
            arrival + REACTION.pulse,
          );
          revealOn(
            [drops[index]],
            { scaleY: 0, opacity: 0 },
            { duration: 0.45, svgOrigin: origin },
            arrival + REACTION.drop,
          );
          // The numeral condenses out of the pulse: it starts low, small and
          // badly out of focus, and resolves as the ring expands past it.
          revealOn(
            [numerals[index]],
            { y: 70, scale: 0.72, opacity: 0, filter: 'blur(16px)' },
            { duration: 1.2, ease: 'power3.out' },
            arrival + REACTION.numeral,
          );
          revealOn(
            [titles[index]],
            { y: 26, opacity: 0, filter: 'blur(8px)' },
            { duration: 0.7 },
            arrival + REACTION.title,
          );
          revealOn(
            [descriptions[index]],
            { y: 18, opacity: 0 },
            { duration: 0.8, ease: 'power2.out' },
            arrival + REACTION.description,
          );
        };

        // -- step one, with the heading ----------------------------------
        const firstTarget = 1 - progress[0];
        revealNode(revealHead, 0, BEAT.firstDot);
        const firstArrival = drawLeg(headTimeline, 1, firstTarget, BEAT.firstDraw);
        react(headTimeline, revealHead, 0, firstArrival);

        // Once step one has had its beat, the line runs on a little past the
        // dot and eases to a stop, so the reader arrives to a journey already
        // under way rather than one parked on its first marker. The scrubbed
        // timeline starts from wherever this leaves the line.
        const runOnTo = firstTarget - RUN_ON * (progress[1] - progress[0]);
        drawLeg(headTimeline, firstTarget, runOnTo, firstArrival + HOLD, 'power2.out');

        // -- steps two and three, by the scrollbar --------------------------
        //
        // The scrubbed timeline opens with a short lead rather than a tween at
        // its very first frame. A tween sitting at t=0 is rendered the moment
        // the trigger measures the timeline, and its first render would write
        // the "drawn to node one" offset over a line the arrival has not yet
        // drawn. The lead also gives the reader a beat with step one before
        // the line moves off.
        let cursor = LEAD;
        let offset = runOnTo;
        let lastArrival = cursor;

        NODES.slice(1).forEach((node, i) => {
          const index = i + 1;
          const target = 1 - progress[index];
          const arrival = drawLeg(timeline, offset, target, cursor);

          // A third of a second ahead of the stroke, so the dot is standing
          // there to be arrived at rather than blinking into existence under a
          // line that has already stopped on it.
          revealNode(reveal, index, arrival - 0.35);
          react(timeline, reveal, index, arrival);

          offset = target;
          lastArrival = arrival;
          cursor = arrival + HOLD;
        });

        // Past the last dot the line runs on to the end of the path, so the
        // journey reads as continuing rather than stopping at step three.
        drawLeg(timeline, offset, 0, lastArrival + 0.5, 'power1.out');

        // Once the line has run out, the whole copy block -- heading, subtitle,
        // description and buttons -- zooms away the way the hero's does, still
        // under the pin, and only then does the section release. Appended here
        // rather than given its own trigger because the block is frozen for
        // the whole pin, and a trigger measured off it would fire at the wrong
        // scroll.
        zoomOutOnScroll(timeline, select('.js-heading'), timeline.duration());

        // Scrubbed by the scrollbar rather than played on enter: the section
        // pins to the viewport and the reader drives the line themselves, one
        // step per stretch of wheel, and only once the last number has landed
        // does the pin release and the page carry on to the next section.
        //
        // `end` is derived from the timeline instead of being a fixed distance,
        // so retiming a beat above re-prices the scroll it costs rather than
        // silently compressing every other beat into what is left. `scrub: 1`
        // is the second the animation takes to catch the scrollbar up, which is
        // what stops a flicked wheel from snapping the line across the screen.
        ScrollTrigger.create({
          trigger: scope,
          start: 'top top',
          end: () => `+=${Math.round(timeline.duration() * SCROLL_PER_SECOND)}`,
          pin: true,
          // Pins late by a hair without it, which shows as the section jumping
          // up by its own top margin at the moment it sticks.
          anticipatePin: 1,
          scrub: 1,
          animation: timeline,
        });
      },
      scope,
    );

    return () => media.revert();
  }, []);

  return (
    <section
      ref={root}
      id="how-it-works"
      // Padding stays at `py-20` above lg rather than opening out to `py-28`:
      // the section is pinned while it plays, so anything taller than the
      // viewport spends the whole pin with its last step hanging below the
      // fold. At the 35rem floor the box plus this padding fits a 720px window.
      className="relative overflow-x-clip py-20"
    >
      {/* The hero's footage ends in black, and at a fractional scroll its
          edge leaks one pixel row past the hero's box; this strip of night
          covers the join. */}
      <div aria-hidden="true" className="bg-night absolute inset-x-0 -top-px h-0.5" />
      <Glow className="top-[30%] -left-40" size={640} />
      <Glow className="top-[10%] -right-32" size={520} tone="lilac" opacity={0.1} />

      <div className="wonder-container wonder-measure relative">
        {/*
          One set of markup for both layouts rather than a desktop copy and a
          mobile copy: duplicating it would put two of every heading and step
          into the DOM, which reads as repeated content to a crawler and to
          anything walking the page linearly.

          Above lg the box takes the full width of the measure and keeps the
          curve's aspect ratio, backing off only on a window too short to stand
          the height that implies -- `wonder-journey-box` in index.css carries
          the reasoning and the three terms that bound it. Below lg the box
          loses its aspect ratio, the curve is dropped, and `position: static`
          makes the browser ignore the inline coordinates without them needing
          to be unset.
        */}
        {/*
          `font-tight` here rather than on each line: the frame sets the whole
          section's copy in Inter Tight, and the only thing that is not -- the
          ghost numerals -- opts back out in JourneyStep.
        */}
        <div className="wonder-journey-box font-tight relative mx-auto">
          <JourneyCurve className="absolute inset-0 hidden h-full w-full lg:block" />

          {/*
            Flush to the box's top-left corner: in the frame the heading's cap
            line and the curve's left end share that edge. The column is wide
            enough for the description to sit on one line, as it does there --
            it runs to about 700px at its full size, and the old `max-w-sm`
            broke it in two.
          */}
          <div className="js-heading lg:absolute lg:top-0 lg:left-0 lg:max-w-3xl">
            {/*
              Straight off the Figma inspector: Inter Tight Medium, 60 on 66,
              letter-spacing -2px. The tracking is written in em so it scales
              with the clamp -- a fixed -2px on the 36px mobile size would be
              nearly twice as tight as the frame's.
            */}
            <h2 className="text-[clamp(2.25rem,3.125vw,3.75rem)] leading-[1.1] font-medium tracking-[-0.0333em]">
              {/*
                Each half gets its own perspective stage rather than the
                overflow-hidden mask the rest of the page uses, because the
                line swings up on its bottom edge instead of sliding up from
                behind one -- a mask here would clip the half that leans out
                of the page. The explicit space between them is load-bearing:
                JSX drops the newline, so without it the accessible name reads
                "How WonderWorks".
              */}
              <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
                <span className="js-head-line text-on-night block">How Wonder</span>
              </span>{' '}
              <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
                <span className="js-head-works text-iris-300 block">Works</span>
              </span>
            </h2>

            <p className="js-subtitle text-on-night mt-6 text-2xl leading-8 font-medium">
              From Profile To Perfect Match
            </p>
            <p className="js-description text-on-night-muted mt-6 text-[clamp(1rem,1.04vw,1.25rem)] leading-relaxed">
              Wonder uses AI to understand what students want and what startups need.
            </p>

            <div className="mt-9 flex flex-wrap gap-4">
              <LinkButton
                to={paths.registerStudent}
                variant="outlineLight"
                shape="pill"
                size="pillMd"
                className="js-cta"
              >
                I&apos;m A Student
                <Icon name="arrowRight" size={16} />
              </LinkButton>
              <LinkButton
                to={paths.registerCompany}
                variant="outlineLight"
                shape="pill"
                size="pillMd"
                className="js-cta"
              >
                I&apos;m A Startup
                <Icon name="arrowRight" size={16} />
              </LinkButton>
            </div>
          </div>

          <ol className="mt-20 space-y-28 lg:mt-0 lg:space-y-0">
            {STEPS.map((item) => (
              <JourneyStep
                key={item.step}
                step={item.step}
                title={item.title}
                description={item.description}
                className="js-step lg:absolute"
                style={atViewBox(item.at[0], item.at[1])}
                numeralClassName={`js-numeral ${item.numeral}`}
              />
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
